import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Bot,
  Plus,
  Loader2,
  Sparkles,
  MoreVertical,
  Copy,
  Trash2,
  Pencil,
  Briefcase,
  Globe,
  Users,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { getEmojiFromCode } from "./Settings";

interface Agent {
  id: string;
  agent_name: string;
  agent_avatar: string | null;
  agent_type: string;
  response_tone: string | null;
  scope_mode: string;
  external_knowledge_allowed: boolean;
  is_active: boolean;
  linked_caso_id: string | null;
  created_at: string;
}

const TYPE_CONFIG: Record<string, { label: string; icon: React.ElementType; variant: "default" | "secondary" | "outline" }> = {
  caso: { label: "Caso", icon: Briefcase, variant: "default" },
  geral: { label: "Geral", icon: Globe, variant: "secondary" },
  compartilhado: { label: "Compartilhado", icon: Users, variant: "outline" },
};

export default function AssessorPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    if (user) loadAgents();
  }, [user]);

  const loadAgents = async () => {
    try {
      // Load ai_agents
      const { data, error } = await supabase
        .from("ai_agents")
        .select("*")
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const agentsList = (data as Agent[]) || [];

      // Check for legacy assessor in assistant_settings
      const { data: legacyData } = await supabase
        .from("assistant_settings")
        .select("*")
        .eq("user_id", user?.id)
        .maybeSingle();

      if (legacyData && !agentsList.some((a) => a.id === `legacy-${legacyData.id}`)) {
        // Add legacy assessor as a virtual agent entry
        agentsList.push({
          id: `legacy-${legacyData.id}`,
          agent_name: legacyData.assistant_name || "Assessor JustiçAI",
          agent_avatar: legacyData.assistant_avatar || null,
          agent_type: "geral",
          response_tone: legacyData.response_tone || null,
          scope_mode: legacyData.scope_mode || "all",
          external_knowledge_allowed: legacyData.external_knowledge_allowed ?? false,
          is_active: legacyData.enabled ?? true,
          linked_caso_id: null,
          created_at: legacyData.created_at,
        });
      }

      setAgents(agentsList);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar assessores");
    } finally {
      setIsLoading(false);
    }
  };

  const createAgent = async () => {
    try {
      const { data, error } = await supabase
        .from("ai_agents")
        .insert({ user_id: user?.id, agent_name: "Novo Assessor", agent_type: "geral" })
        .select()
        .single();
      if (error) throw error;
      toast.success("Assessor criado!");
      navigate(`/assessor/config/${(data as Agent).id}`);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao criar assessor");
    }
  };

  const duplicateAgent = async (agent: Agent) => {
    try {
      const { data, error } = await supabase
        .from("ai_agents")
        .insert({
          user_id: user?.id,
          agent_name: `${agent.agent_name} (cópia)`,
          agent_avatar: agent.agent_avatar,
          agent_type: agent.agent_type,
          response_tone: agent.response_tone,
          scope_mode: agent.scope_mode,
          external_knowledge_allowed: agent.external_knowledge_allowed,
          is_active: false,
          linked_caso_id: agent.linked_caso_id,
        })
        .select()
        .single();
      if (error) throw error;
      toast.success("Assessor duplicado!");
      loadAgents();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao duplicar assessor");
    }
  };

  const deleteAgent = async () => {
    if (!deleteId) return;
    try {
      const { error } = await supabase.from("ai_agents").delete().eq("id", deleteId);
      if (error) throw error;
      setAgents((prev) => prev.filter((a) => a.id !== deleteId));
      toast.success("Assessor excluído");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao excluir assessor");
    } finally {
      setDeleteId(null);
    }
  };

  const toggleActive = async (agent: Agent) => {
    try {
      const { error } = await supabase
        .from("ai_agents")
        .update({ is_active: !agent.is_active })
        .eq("id", agent.id);
      if (error) throw error;
      setAgents((prev) =>
        prev.map((a) => (a.id === agent.id ? { ...a, is_active: !a.is_active } : a))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const renderAvatar = (agent: Agent) => {
    if (agent.agent_avatar?.startsWith("emoji:")) {
      return (
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-2xl">
          {getEmojiFromCode(agent.agent_avatar)}
        </div>
      );
    }
    if (agent.agent_avatar) {
      return (
        <img
          src={agent.agent_avatar}
          alt={agent.agent_name}
          className="w-14 h-14 rounded-full object-cover"
        />
      );
    }
    return (
      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
        <Sparkles className="h-6 w-6 text-primary" />
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Meus Assessores</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gerencie seus assessores de IA para casos, tarefas gerais e trabalhos compartilhados.
          </p>
        </div>
        <Button onClick={createAgent}>
          <Plus className="mr-2 h-4 w-4" />
          Criar Assessor
        </Button>
      </div>

      {/* Grid */}
      {agents.length === 0 ? (
        <Card className="flex flex-col items-center justify-center p-12 text-center border-dashed">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Bot className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-1">Nenhum assessor criado</h3>
          <p className="text-muted-foreground text-sm mb-4 max-w-sm">
            Crie seu primeiro assessor de IA para ajudar com seus casos e tarefas jurídicas.
          </p>
          <Button onClick={createAgent}>
            <Plus className="mr-2 h-4 w-4" />
            Criar Primeiro Assessor
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {agents.map((agent) => {
            const isLegacy = agent.id.startsWith("legacy-");
            const typeConf = TYPE_CONFIG[agent.agent_type] || TYPE_CONFIG.geral;
            const TypeIcon = typeConf.icon;
            return (
              <Card
                key={agent.id}
                className="relative group cursor-pointer hover:shadow-md transition-shadow p-5"
                onClick={() => navigate(isLegacy ? "/configuracoes/assessor" : `/assessor/config/${agent.id}`)}
              >
                {/* Top row: avatar + actions */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {renderAvatar(agent)}
                    <div className="min-w-0">
                      <h3 className="font-semibold truncate">{agent.agent_name}</h3>
                      <Badge variant={typeConf.variant} className="mt-1 text-xs">
                        <TypeIcon className="mr-1 h-3 w-3" />
                        {typeConf.label}
                      </Badge>
                    </div>
                  </div>

                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {!isLegacy && (
                      <Switch
                        checked={agent.is_active}
                        onCheckedChange={() => toggleActive(agent)}
                        aria-label="Ativar/desativar assessor"
                      />
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(isLegacy ? "/configuracoes/assessor" : `/assessor/config/${agent.id}`)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar
                        </DropdownMenuItem>
                        {!isLegacy && (
                          <>
                            <DropdownMenuItem onClick={() => duplicateAgent(agent)}>
                              <Copy className="mr-2 h-4 w-4" />
                              Duplicar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleteId(agent.id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Excluir
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Status indicator */}
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <span
                    className={`h-2 w-2 rounded-full ${agent.is_active ? "bg-green-500" : "bg-muted-foreground/40"}`}
                  />
                  {agent.is_active ? "Ativo" : "Inativo"}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir assessor?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação não pode ser desfeita. O assessor será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={deleteAgent} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
